import {
  ActionIcon,
  Chip,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTiktok,
  IconPlus,
  IconTrash,
  IconWorld,
} from "@tabler/icons-react";
import { generateText } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useCallback } from "react";
import type { PublicProfileInput, SocialLinkInput } from "@/api-types";
import { specialtyOptions } from "@/pages/trainer/profile/common";
import {
  getLanguageInfo,
  getLanguageName,
  LANGUAGE_OPTIONS,
} from "@/utils/languages";
import { ToggleEditor } from "./ToggleEditor";

export type PublicProfileFormMode = "full" | "minimal";

// --- Validators ---
const REQUIRED_FIELD_MSG = "This field is required.";
const REQUIRED_LIST_MSG = "This list cannot be empty.";
const NON_EMPTY_LIST_ITEMS_MSG = "All list items must be filled.";
const NON_EMPTY_SOCIAL_LINKS_MSG = "All social links must be filled.";

const requiredString = (value: string) => {
  if (value.trim() === "") return REQUIRED_FIELD_MSG;
  return null;
};

const requiredRichText = (value: string) => {
  let textContent = value;
  if (value.startsWith("{")) {
    const jsonContent = JSON.parse(value);
    textContent = generateText(jsonContent, [StarterKit]);
  }
  if (textContent.trim() === "") {
    return REQUIRED_FIELD_MSG;
  }
};

const requiredList = (value: string[]) => {
  if (value.length === 0) return REQUIRED_LIST_MSG;
  return null;
};

const nonEmptyListItems = (value: string[]) => {
  if (value.some((v) => v.trim() === "")) return NON_EMPTY_LIST_ITEMS_MSG;
  return null;
};

const nonEmptySocialLinks = (
  value: { site: string; value: string }[] | undefined,
) => {
  if (!value) return null;
  if (value.some((v) => v.site.trim() === "" || v.value.trim() === ""))
    return NON_EMPTY_SOCIAL_LINKS_MSG;
  return null;
};

interface UsePublicProfileFormProps {
  initialValues: PublicProfileInput;
  mode?: PublicProfileFormMode;
  onFormSubmit: (values: PublicProfileInput, isPreview: boolean) => void;
}

export const usePublicProfileForm = ({
  initialValues,
  mode = "full",
  onFormSubmit,
}: UsePublicProfileFormProps) => {
  const fullValidate = {
    headline: requiredString,
    description: requiredRichText,
    specialities: (value: string[]) =>
      requiredList(value) || nonEmptyListItems(value),
    languages: (value: string[]) =>
      requiredList(value) || nonEmptyListItems(value),
    testimonials: nonEmptyListItems,
    education: (value: string[]) =>
      requiredList(value) || nonEmptyListItems(value),
    links: (value: { site: string; value: string }[] | undefined) => {
      return value && value.length > 0 ? nonEmptySocialLinks(value) : null;
    },
  };

  const minimalValidate = {
    specialities: nonEmptyListItems,
    languages: nonEmptyListItems,
    testimonials: nonEmptyListItems,
    education: nonEmptyListItems,
    links: (value: { site: string; value: string }[] | undefined) => {
      return value && value.length > 0 ? nonEmptySocialLinks(value) : null;
    },
  };

  const form = useForm<PublicProfileInput>({
    mode: "uncontrolled",
    initialValues,
    validate: mode === "full" ? fullValidate : minimalValidate,
  });

  const submit = useCallback(
    (isPreview: boolean = false) => {
      form.validate();
      if (!form.isValid()) {
        return false;
      }
      form.onSubmit((values, _e) => {
        onFormSubmit(values, isPreview);
      })();

      return true;
    },
    [form, onFormSubmit],
  );

  const renderedForm = (
    <form>
      <Stack gap="md">
        <TextInput
          label="Headline"
          placeholder="Your short introduction"
          key={form.key("headline")}
          {...form.getInputProps("headline")}
        />

        <ToggleEditor
          label="About me"
          placeholder="Share your experience, approach, and philosophy about dog training"
          value={form.values.description}
          onChange={(value) => form.setFieldValue("description", value)}
          error={form.errors.description}
          minRows={5}
        />

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Specialities
          </Text>
          <Chip.Group
            multiple
            key={form.key("specialities")}
            {...form.getInputProps("specialities")}
          >
            <Group>
              {specialtyOptions.map((option) => (
                <Chip color="third.6" key={option.value} value={option.value}>
                  <Text fw={500}>{option.label}</Text>
                </Chip>
              ))}
            </Group>
          </Chip.Group>
          {form.errors.specialities && (
            <div
              style={{
                color: "var(--mantine-color-error)",
                fontSize: "var(--mantine-font-size-xs)",
              }}
            >
              {form.errors.specialities}
            </div>
          )}
        </Stack>

        {/* Languages */}
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              Languages
            </Text>
            <Select
              placeholder="Add a language"
              data={LANGUAGE_OPTIONS.filter(
                (lang) =>
                  !((form.values.languages || []) as string[]).includes(
                    lang.value,
                  ),
              )}
              searchable
              clearable
              value=""
              onChange={(value) => {
                if (
                  value &&
                  !form.values.languages?.includes(value) &&
                  form.values.languages?.length < 10
                ) {
                  form.insertListItem("languages", value);
                }
              }}
              renderOption={({ option }) => (
                <Group gap="xs">
                  <Text>{getLanguageInfo(option.value)?.emoji}</Text>
                  <Text>{getLanguageName(option.value)}</Text>
                </Group>
              )}
              style={{ width: 200 }}
            />
          </Group>
          <Stack gap="xs">
            {Array.isArray(form.values.languages) &&
              form.values.languages.map((lang, index) => {
                const langInfo = getLanguageInfo(lang);
                return (
                  <Group key={lang} justify="space-between" align="center">
                    <Group gap="xs">
                      <Text>{langInfo?.emoji}</Text>
                      <Text>{getLanguageName(lang)}</Text>
                    </Group>
                    <ActionIcon
                      color="red"
                      variant="light"
                      size="sm"
                      onClick={() => {
                        form.removeListItem("languages", index);
                      }}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                );
              })}
          </Stack>
          {form.errors.languages && (
            <div
              style={{
                color: "var(--mantine-color-error)",
                fontSize: "var(--mantine-font-size-xs)",
              }}
            >
              {form.errors.languages}
            </div>
          )}
        </Stack>

        {/* Testimonials */}
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              Testimonials
            </Text>
            <ActionIcon
              variant="light"
              size="sm"
              onClick={() => {
                if ((form.values.testimonials || []).length < 20) {
                  form.insertListItem("testimonials", "");
                }
              }}
              disabled={(form.values.testimonials || []).length >= 20}
            >
              <IconPlus size={16} />
            </ActionIcon>
          </Group>
          <Stack gap="xs">
            {(form.values.testimonials || []).map((_, index) => (
              <Group key={index} align="flex-start">
                <Textarea
                  placeholder="Enter a testimonial from a client"
                  style={{ flex: 1 }}
                  minRows={2}
                  key={form.key(`testimonials.${index}`)}
                  {...form.getInputProps(`testimonials.${index}`)}
                />
                <ActionIcon
                  color="red"
                  variant="light"
                  size="sm"
                  onClick={() => {
                    form.removeListItem("testimonials", index);
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
          {form.errors.testimonials && (
            <div
              style={{
                color: "var(--mantine-color-error)",
                fontSize: "var(--mantine-font-size-xs)",
              }}
            >
              {form.errors.testimonials}
            </div>
          )}
          {(form.values.testimonials || []).length === 0 && (
            <Text size="xs" c="dimmed">
              Add testimonials from your existin clients
            </Text>
          )}
        </Stack>

        {/* Education */}
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              Education & Certifications
            </Text>
            <ActionIcon
              variant="light"
              size="sm"
              onClick={() => {
                if ((form.values.education || []).length < 20) {
                  form.insertListItem("education", "");
                }
              }}
              disabled={(form.values.education || []).length >= 20}
            >
              <IconPlus size={16} />
            </ActionIcon>
          </Group>
          <Stack gap="xs">
            {(form.values.education || []).map((_, index) => (
              <Group key={index} align="flex-start">
                <TextInput
                  placeholder="Enter education or certification"
                  style={{ flex: 1 }}
                  key={form.key(`education.${index}`)}
                  {...form.getInputProps(`education.${index}`)}
                />
                <ActionIcon
                  color="red"
                  variant="light"
                  size="sm"
                  onClick={() => {
                    form.removeListItem("education", index);
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
          {form.errors.education && (
            <div
              style={{
                color: "var(--mantine-color-error)",
                fontSize: "var(--mantine-font-size-xs)",
              }}
            >
              {form.errors.education}
            </div>
          )}
          {(form.values.education || []).length === 0 && (
            <Text size="xs" c="dimmed">
              Add your educational background and certifications
            </Text>
          )}
        </Stack>

        {/* Social Links */}
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              Social Links
            </Text>
            <Select
              placeholder="Add a social link"
              data={[
                { value: "instagram", label: "Instagram" },
                { value: "facebook", label: "Facebook" },
                { value: "tiktok", label: "TikTok" },
                { value: "generic", label: "Website" },
              ]}
              value=""
              onChange={(value) => {
                if (value && (form.values.links || []).length < 10) {
                  form.insertListItem("links", { site: value, value: "" });
                }
              }}
              renderOption={({ option }) => {
                const getSiteIcon = (site: string) => {
                  switch (site) {
                    case "instagram":
                      return <IconBrandInstagram size={16} />;
                    case "facebook":
                      return <IconBrandFacebook size={16} />;
                    case "tiktok":
                      return <IconBrandTiktok size={16} />;
                    default:
                      return <IconWorld size={16} />;
                  }
                };
                return (
                  <Group gap="xs">
                    {getSiteIcon(option.value)}
                    <Text>{option.label}</Text>
                  </Group>
                );
              }}
              style={{ width: 180 }}
            />
          </Group>
          <Stack gap="xs">
            {Array.isArray(form.values.links) &&
              form.values.links.map((link: SocialLinkInput, index: number) => {
                const getSiteIcon = (site: string) => {
                  switch (site) {
                    case "instagram":
                      return <IconBrandInstagram size={20} />;
                    case "facebook":
                      return <IconBrandFacebook size={20} />;
                    case "tiktok":
                      return <IconBrandTiktok size={20} />;
                    default:
                      return <IconWorld size={20} />;
                  }
                };

                const getPlaceholder = (site: string) => {
                  switch (site) {
                    case "instagram":
                      return "username or full URL";
                    case "facebook":
                      return "username or full URL";
                    case "tiktok":
                      return "username or full URL";
                    default:
                      return "https://yourwebsite.com";
                  }
                };

                return (
                  <Group key={index} align="flex-start">
                    <Group gap="xs" style={{ flex: 1 }}>
                      {getSiteIcon(link.site)}
                      <TextInput
                        placeholder={getPlaceholder(link.site)}
                        key={form.key(`links.${index}.value`)}
                        {...form.getInputProps(`links.${index}.value`)}
                        style={{ flex: 1 }}
                      />
                    </Group>
                    <ActionIcon
                      color="red"
                      variant="light"
                      size="sm"
                      onClick={() => {
                        form.removeListItem("links", index);
                      }}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                );
              })}
          </Stack>
          {form.errors.links && (
            <div
              style={{
                color: "var(--mantine-color-error)",
                fontSize: "var(--mantine-font-size-xs)",
              }}
            >
              {form.errors.links}
            </div>
          )}
        </Stack>
      </Stack>
    </form>
  );

  return {
    form: renderedForm,
    submit,
  };
};
